import { FC, useCallback, useState } from "react";

import {
  Button,
  ButtonGroup,
  Heading,
  HStack,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Stack,
  Link,
} from "@chakra-ui/react";
import { QRCodeSVG } from "qrcode.react";
import { androidAppDownloadLink, iosAppDownloadLink } from "utils/constants";
import { userAgentIsAndroid, userAgentIsIOS, userAgentIsMobile } from "utils/device";
import amplitude from "amplitude-js";

interface GetAppModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const GetAppModal: FC<GetAppModalProps> = ({ isOpen, onClose }) => {
  const initialTabIndex = userAgentIsMobile ? (userAgentIsAndroid ? 0 : 1) : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent width={320}>
        <ModalHeader pt="12px" pb="2px">
          Get Reclaim app
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Tabs defaultIndex={initialTabIndex}>
            <TabList>
              <Tab>Android</Tab>
              <Tab>iOS</Tab>
            </TabList>

            <TabPanels>
              <TabPanel as={Stack} height={250} justify="center" align="center" spacing={6}>
                <QRCodeSVG value={androidAppDownloadLink} />

                <Link color="blue.400" target="_blank" href={androidAppDownloadLink}>
                  Click here to open in Google Play Store.
                </Link>
              </TabPanel>

              <TabPanel as={Stack} height={250} justify="center" align="center" spacing={6}>
                <QRCodeSVG value={iosAppDownloadLink} />

                <Link color="blue.400" target="_blank" href={iosAppDownloadLink}>
                  Click here to open in Apple's App Store.
                </Link>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface DialogProps {
  onOpenAppClick: () => void;
}

const OpenInAppDialog: FC<DialogProps> = ({ onOpenAppClick }) => {
  const [isGetAppModalOpen, setIsGetAppModalOpen] = useState(false);

  const onGetAppClick = useCallback(() => {
    amplitude.getInstance().logEvent("Clicked on get app");
    if (userAgentIsMobile) {
      if (userAgentIsAndroid) {
        window.open(androidAppDownloadLink, "_self", "noopener,noreferrer");
      } else if (userAgentIsIOS) {
        window.open(iosAppDownloadLink, "_self", "noopener,noreferrer");
      }
    }

    // we are not sure what platform we are on, so just let user choose anyway as fallback if the previous auto-open didn't work...
    setIsGetAppModalOpen(true);
  }, []);

  return (
    <VStack
      bg="gray.100"
      px={3}
      pt={6}
      pb={4}
      borderRadius={24}
      maxWidth="400px"
      width="calc(100% - 24px)"
      spacing={6}
      position="fixed"
      left="50%"
      right="12px"
      bottom="16px"
      transform="translateX(-50%)"
    >
      <HStack align="center">
        <Image
          boxSize="60px"
          src="/icons/reclaim-app.svg"
          alt="get reclaim app"
          objectFit="contain"
          mx={2}
          borderRadius='16px'
        />

        <VStack align="flex-start" spacing="4px">
          <Heading as="h2" fontSize="16px">
            Submit on our mobile app
          </Heading>

          <Text color="gray.600" fontSize="15px" lineHeight="17px">
            To submit the claim, you need to use the Reclaim mobile app.
          </Text>
        </VStack>
      </HStack>

      <ButtonGroup width="full" justifyContent="space-around">
        <Button
          bg="blue"
          color="white"
          _hover={{ opacity: 0.8, color: "gray.400" }}
          borderRadius="12px"
          width="150px"
          height="44px"
          onClick={onGetAppClick}
        >
          Get app
        </Button>

        <Button
          bg="gray.300"
          color="black"
          opacity={0.8}
          _hover={{ opacity: 1 }}
          borderRadius="12px"
          width="150px"
          height="44px"
          onClick={onOpenAppClick}
        >
          Open app
        </Button>
      </ButtonGroup>

      <GetAppModal isOpen={isGetAppModalOpen} onClose={() => setIsGetAppModalOpen(false)} />
    </VStack>
  );
};

export default OpenInAppDialog;
